import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, Zap, Trash2, Power } from "lucide-react";

const SEVERITIES = ["critical", "high", "medium", "low"];

export default function Automation() {
  const [rules, setRules] = useState([]);
  const [cat, setCat] = useState({ triggers: [], actions: [] });
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({
    name: "",
    trigger: "log_ingested",
    enabled: true,
    conditions: { severity: { in: ["critical", "error"] } },
    actions: [{ type: "create_incident", title: "Auto-incident from {host}", description: "Detected: {message}", severity: "high" }],
  });
  const [rawConditions, setRawConditions] = useState('{"severity":{"in":["critical","error"]}}');

  const load = async () => {
    try {
      const [r, c] = await Promise.all([api.get("/automation/rules"), api.get("/automation/catalog")]);
      setRules(r.data);
      setCat(c.data);
    } catch (_e) { toast.error("Failed to load rules"); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    let parsedConditions = {};
    try { parsedConditions = JSON.parse(rawConditions || "{}"); }
    catch { return toast.error("Conditions is not valid JSON"); }
    try {
      await api.post("/automation/rules", { ...form, conditions: parsedConditions });
      toast.success("Rule created");
      setShowCreate(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed");
    }
  };

  const toggle = async (r) => {
    try {
      await api.patch(`/automation/rules/${r.id}?enabled=${!r.enabled}`);
      load();
    } catch (_e) { toast.error("Toggle failed"); }
  };
  const remove = async (r) => {
    if (!confirm(`Delete rule '${r.name}'?`)) return;
    try { await api.delete(`/automation/rules/${r.id}`); load(); }
    catch (_e) { toast.error("Delete failed"); }
  };

  const updateAction = (i, field, value) => {
    const acts = [...form.actions];
    acts[i] = { ...acts[i], [field]: value };
    setForm({ ...form, actions: acts });
  };
  const addAction = () => setForm({ ...form, actions: [...form.actions, { type: "notify", message: "" }] });
  const removeAction = (i) => setForm({ ...form, actions: form.actions.filter((_, idx) => idx !== i) });

  return (
    <div className="p-8 max-w-[1400px] mx-auto">
      <header className="flex items-end justify-between mb-6">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-widest text-[#8E9196] flex items-center gap-2">
            <Zap className="w-3.5 h-3.5" /> Rules engine
          </div>
          <h1 className="font-display font-black text-5xl mt-2">Automation</h1>
          <p className="text-[#8E9196] mt-1">When X happens, do Y. Rules evaluate on every event from users and API-key ingest.</p>
        </div>
        <button onClick={() => setShowCreate(true)} data-testid="new-rule-btn"
          className="inline-flex items-center gap-2 border border-[#00F0FF] text-[#00F0FF] hover:bg-[#00F0FF] hover:text-black px-4 py-2.5 rounded-sm font-mono uppercase text-xs tracking-widest">
          <Plus className="w-4 h-4" /> New rule
        </button>
      </header>

      <div className="border border-white/10 bg-[#121212] rounded-sm overflow-hidden mb-6" data-testid="rules-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/10 text-[10px] font-mono uppercase tracking-widest text-[#8E9196]">
              <th className="text-left px-5 py-3">Name</th>
              <th className="text-left px-5 py-3">Trigger</th>
              <th className="text-left px-5 py-3">Conditions</th>
              <th className="text-left px-5 py-3">Actions</th>
              <th className="text-left px-5 py-3">Fired</th>
              <th className="text-left px-5 py-3">Last fired</th>
              <th className="text-left px-5 py-3">State</th>
              <th className="text-right px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {rules.length === 0 && (
              <tr><td colSpan={8} className="text-center py-10 text-sm text-[#52525B]">No rules — auto-triggers (IOC ingest → investigation) still work out-of-the-box.</td></tr>
            )}
            {rules.map((r) => (
              <tr key={r.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`rule-row-${r.id}`}>
                <td className="px-5 py-3">{r.name}</td>
                <td className="px-5 py-3 font-mono text-[10px] uppercase tracking-widest text-[#00F0FF]">{r.trigger}</td>
                <td className="px-5 py-3 font-mono text-[10px] text-[#8E9196] max-w-xs truncate">{JSON.stringify(r.conditions)}</td>
                <td className="px-5 py-3">
                  <div className="flex flex-wrap gap-1">
                    {(r.actions || []).map((a, i) => (
                      <span key={i} className="font-mono text-[9px] uppercase tracking-widest border border-white/15 rounded-sm px-1.5 py-0.5">{a.type}</span>
                    ))}
                  </div>
                </td>
                <td className="px-5 py-3 font-mono text-xs">{r.fire_count}</td>
                <td className="px-5 py-3 font-mono text-[10px] text-[#52525B]">{r.last_fired_at ? new Date(r.last_fired_at).toLocaleString() : "—"}</td>
                <td className="px-5 py-3">
                  <button onClick={() => toggle(r)} data-testid={`toggle-${r.id}`}
                    className={`inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 border rounded-sm ${
                      r.enabled ? "border-[#34C759] text-[#34C759]" : "border-[#8E9196] text-[#8E9196]"
                    }`}>
                    <Power className="w-3 h-3" /> {r.enabled ? "enabled" : "off"}
                  </button>
                </td>
                <td className="px-5 py-3 text-right">
                  <button onClick={() => remove(r)} data-testid={`delete-rule-${r.id}`} className="p-1.5 text-[#8E9196] hover:text-[#FF3B30]">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="border border-white/10 bg-[#0a0a0a] rounded-sm p-5 mb-6">
        <div className="text-[11px] font-mono uppercase tracking-widest text-[#8E9196]">Built-in auto-triggers (always on)</div>
        <ul className="mt-3 space-y-2 text-sm">
          <li>› <span className="text-[#00F0FF]">IOC ingested</span> via API-key with <code className="font-mono text-xs">type: &quot;ioc&quot;</code> → auto-creates an investigation, enrichment + AI analysis, PDF report</li>
          <li>› <span className="text-[#00F0FF]">Incident created</span> → automation rules evaluated (optionally auto-run playbook via a rule)</li>
        </ul>
      </div>

      {showCreate && (
        <div className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm flex items-center justify-center p-6" onClick={() => setShowCreate(false)}>
          <form onSubmit={create} onClick={(e) => e.stopPropagation()}
            data-testid="create-rule-modal"
            className="bg-[#121212] border border-white/15 rounded-sm w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <div className="font-display text-xl">New automation rule</div>
              <button type="button" onClick={() => setShowCreate(false)} className="text-[#8E9196] hover:text-white"><X className="w-4 h-4" /></button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-[11px] font-mono uppercase tracking-widest text-[#8E9196] mb-1.5">Name</label>
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="rule-name"
                  placeholder="Auto-incident on critical logs"
                  className="w-full bg-transparent border border-white/15 px-3 py-2 rounded-sm font-mono text-sm outline-none focus:border-[#00F0FF]" />
              </div>
              <div>
                <label className="block text-[11px] font-mono uppercase tracking-widest text-[#8E9196] mb-1.5">Trigger</label>
                <select value={form.trigger} onChange={(e) => setForm({ ...form, trigger: e.target.value })}
                  data-testid="rule-trigger"
                  className="w-full bg-[#0a0a0a] border border-white/15 px-3 py-2 rounded-sm font-mono text-sm outline-none focus:border-[#00F0FF]">
                  {(cat.triggers || ["log_ingested", "ioc_ingested", "incident_created"]).map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-mono uppercase tracking-widest text-[#8E9196] mb-1.5">Conditions (JSON)</label>
                <textarea rows={4} value={rawConditions} onChange={(e) => setRawConditions(e.target.value)}
                  data-testid="rule-conditions"
                  className="w-full bg-transparent border border-white/15 px-3 py-2 rounded-sm font-mono text-xs outline-none focus:border-[#00F0FF]" />
                <div className="text-[10px] text-[#52525B] mt-1">Supports: <code>{'{"field":"eq"}'}</code>, <code>{'{"field":{"in":[...]}}'}</code>, <code>{'{"field":{"contains":"..."}}'}</code>, <code>{'{"field":{"regex":"..."}}'}</code></div>
              </div>

              <div>
                <label className="block text-[11px] font-mono uppercase tracking-widest text-[#8E9196] mb-1.5">Actions</label>
                <div className="space-y-2">
                  {form.actions.map((a, i) => (
                    <div key={i} className="border border-white/10 rounded-sm p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <select value={a.type} onChange={(e) => updateAction(i, "type", e.target.value)}
                          data-testid={`rule-action-type-${i}`}
                          className="bg-[#0a0a0a] border border-white/15 px-3 py-1 rounded-sm font-mono text-xs uppercase tracking-widest outline-none focus:border-[#00F0FF]">
                          {(cat.actions || ["create_incident", "create_investigation", "notify"]).map((t) => (
                            <option key={t} value={t}>{t}</option>
                          ))}
                        </select>
                        {form.actions.length > 1 && (
                          <button type="button" onClick={() => removeAction(i)} className="text-[#8E9196] hover:text-[#FF3B30]"><X className="w-4 h-4" /></button>
                        )}
                      </div>
                      {a.type === "create_incident" && (
                        <div className="grid grid-cols-3 gap-2">
                          <input placeholder="title (supports {host} {message})" value={a.title || ""} onChange={(e) => updateAction(i, "title", e.target.value)}
                            className="col-span-2 bg-transparent border border-white/15 px-3 py-1.5 rounded-sm font-mono text-xs outline-none focus:border-[#00F0FF]" />
                          <select value={a.severity || "medium"} onChange={(e) => updateAction(i, "severity", e.target.value)}
                            className="bg-[#0a0a0a] border border-white/15 px-3 py-1.5 rounded-sm font-mono text-xs outline-none focus:border-[#00F0FF]">
                            {SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
                          </select>
                          <input placeholder="description template" value={a.description || ""} onChange={(e) => updateAction(i, "description", e.target.value)}
                            className="col-span-3 bg-transparent border border-white/15 px-3 py-1.5 rounded-sm font-mono text-xs outline-none focus:border-[#00F0FF]" />
                        </div>
                      )}
                      {a.type === "create_investigation" && (
                        <input placeholder="fallback customer_name" value={a.customer_name || ""} onChange={(e) => updateAction(i, "customer_name", e.target.value)}
                          className="w-full bg-transparent border border-white/15 px-3 py-1.5 rounded-sm font-mono text-xs outline-none focus:border-[#00F0FF]" />
                      )}
                      {a.type === "notify" && (
                        <input placeholder="message (supports {host} {message})" value={a.message || ""} onChange={(e) => updateAction(i, "message", e.target.value)}
                          className="w-full bg-transparent border border-white/15 px-3 py-1.5 rounded-sm font-mono text-xs outline-none focus:border-[#00F0FF]" />
                      )}
                    </div>
                  ))}
                  <button type="button" onClick={addAction} className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-widest text-[#00F0FF] hover:underline">
                    <Plus className="w-3 h-3" /> Add action
                  </button>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowCreate(false)} className="border border-white/15 text-[#8E9196] hover:text-white px-3 py-2 rounded-sm font-mono uppercase text-xs tracking-widest">Cancel</button>
                <button data-testid="create-rule-submit" className="border border-[#00F0FF] text-[#00F0FF] hover:bg-[#00F0FF] hover:text-black px-3 py-2 rounded-sm font-mono uppercase text-xs tracking-widest">Create rule</button>
              </div>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
